import { z } from "zod"
import { CountryCode, CurrencyCode, CurrencySymbol } from "../country/countryList"

// Order matters: OrgService.upgradeTier treats a LOWER index → HIGHER index move as an
// upgrade. Start..Enterprise are legacy plans (kept because org rows still store these
// names); Core..Elevate are the current sellable plans, appended in rank order.
export const TierNames = ['Trial', 'Start', 'Focus', 'Scale', 'Enterprise', 'Core', 'Grow', 'Expand', 'Accelerate', 'Elevate'] as const
export type TierNamesDto = (typeof TierNames)[number]
export const tierNameDtoSchema = z.enum(TierNames)

type Price = Partial<Record<CountryCode, {
   symbol: CurrencySymbol,
   price: number,
   currencyCode: CurrencyCode
 }>>

export type TierDto = {
   name: TierNamesDto
   pricing?: Price
   isLastTier?: boolean
   excludeFromRecommendations?: boolean
   maxUsers?: number
   minRecommendedUsers?: number
   /** No longer sellable — kept only because existing org rows still store the name. */
   legacy?: boolean
}

const getPrice = (countryCode: CountryCode, price: number): Price => {
   switch (countryCode) {
      case 'NZ':
         return {
            NZ: {
               symbol: '$',
               price,
               currencyCode: 'NZD'
            }
         }
      case 'AU':
         return {
            AU: {
               symbol: '$',
               price,
               currencyCode: 'AUD'
            }
         }
      case 'GB':
         return {
            GB: {
               symbol: '£',
               price,
               currencyCode: 'GBP'
            }
         }
      case 'CA':
         return {
            CA: {
               symbol: '$',
               price,
               currencyCode: 'CAD'
            }
         }
      case 'US':
         return {
            US: {
               symbol: '$',
               price,
               currencyCode: 'USD'
            }
         }
      default:
         throw new Error('Non supported country')
   }
}

export const defaultTrialTier: TierDto = {
   name: 'Trial',
   maxUsers: 100,
}

export const tiers: TierDto[] = [
   defaultTrialTier,
   {
      name: 'Start',
      legacy: true,
      maxUsers: 2,
      excludeFromRecommendations: true,
      pricing: {
         ...getPrice('NZ', 495),
         ...getPrice('AU', 495),
         ...getPrice('GB', 245),
         ...getPrice('CA', 450),
         ...getPrice('US', 335),
      }
   },
   {
      name: 'Focus',
      legacy: true,
      maxUsers: 5,
      pricing: {
         ...getPrice('NZ', 695),
         ...getPrice('AU', 695),
         ...getPrice('GB', 345),
         ...getPrice('CA', 630),
         ...getPrice('US', 465),
      }
   },
   {
      name: 'Scale',
      legacy: true,
      maxUsers: 10,
      pricing: {
         ...getPrice('NZ', 895),
         ...getPrice('AU', 895),
         ...getPrice('GB', 445),
         ...getPrice('CA', 830),
         ...getPrice('US', 625),
      }
   },
   {
      name: 'Enterprise',
      legacy: true,
      isLastTier: true,
      minRecommendedUsers: 11,
      maxUsers: 100,
   },
   // Current sellable plans. Pricing and feature bullets live in the frontend static plan
   // catalogue (features/org/edit/billing/static-plan-catalog.ts) until Chargebee (PR #471)
   // becomes the catalogue source, so these entries only carry the seat ceiling.
   {
      name: 'Core',
      maxUsers: 1,
   },
   {
      name: 'Grow',
      maxUsers: 1,
   },
   {
      name: 'Expand',
      maxUsers: 3,
   },
   {
      name: 'Accelerate',
      maxUsers: 5,
   },
   {
      name: 'Elevate',
      maxUsers: 10,
   }
]

export const getBillingTextPerCurrency = (currencyCode: CurrencyCode): string => {
   switch (currencyCode) {
      case 'NZD':
         return 'Billed in NZD, plus GST'
      case 'CAD':
         return 'Billed in CAD, exclusive of sales tax'
      default:
         return `Billed in ${currencyCode}`
   }
}
